import { DateTime } from 'luxon';

const DATE_TIME_FORMAT = 'dd/MM/yyyy HH:mm';
const TIME_FORMAT = 'HH:mm';

/**
 * Formats a given input string into a valid time format (HH:mm) while ensuring it adheres to constraints.
 * If the input forms a complete time, it finds the closest valid time from a provided list.
 *
 * @param {string} value - The raw input string containing time digits.
 * @param {string[]} validTimes - An array of valid time strings in "HH:mm" format.
 * @returns {string} - The formatted time or the closest valid time.
 */
export function timeMask(value: string, validTimes: string[]): string {
  if (!value?.length) {
    return '';
  }

  const digits = value.replace(/\D/g, '');

  let hours = digits.substring(0, 2);
  let minutes = digits.substring(2, 4);

  // Ensure proper hour formatting:
  // - If only one digit is provided and it is greater than '2', prepend '0' to keep it valid.
  // - If two digits are provided but exceed 23, cap it at '23'.
  if (hours.length === 1 && parseInt(hours, 10) > 2) {
    hours = '0' + hours;
  } else if (hours.length === 2 && parseInt(hours, 10) > 23) {
    hours = '23';
  }

  if (hours.length === 2 && minutes.length === 2) {
    if (parseInt(minutes, 10) >= 60) {
      minutes = '59';
    }

    const inputDateTime = DateTime.fromFormat(`${hours}:${minutes}`, TIME_FORMAT);

    // Find the closest valid time by comparing the absolute difference in minutes
    return validTimes.reduce((prev, curr) =>
      Math.abs(DateTime.fromFormat(curr, TIME_FORMAT).diff(inputDateTime, 'minutes').minutes) <
      Math.abs(DateTime.fromFormat(prev, TIME_FORMAT).diff(inputDateTime, 'minutes').minutes)
        ? curr
        : prev
    );
  }

  if (minutes.length) {
    return `${hours}:${minutes}`;
  }

  return hours;
}

/**
 * Formats a given input string or DateTime object into a valid date-time format (dd/MM/yyyy HH:mm).
 * Ensures date and time components are correctly formatted and constrained within valid ranges.
 *
 * @param {string | DateTime} value - The raw input string or a DateTime object.
 * @param {string[]} validTimes - An array of valid time strings in "HH:mm" format.
 * @returns {string} - The formatted date-time string.
 */
export function dateTimeMask(value: string | DateTime, validTimes: string[]): string {
  if (value && typeof value !== 'string') {
    return value.toFormat(DATE_TIME_FORMAT);
  }

  if (!value?.length) {
    return '';
  }

  const digits = value.replace(/\D/g, '');

  let day = digits.substring(0, 2);
  let month = digits.substring(2, 4);
  let year = digits.substring(4, 8);

  if (day.length === 1 && parseInt(day, 10) > 3) {
    day = '0' + day;
  } else if (day.length === 2 && parseInt(day, 10) > 31) {
    day = '31';
  }

  if (month.length === 1 && parseInt(month, 10) > 1) {
    month = '0' + month;
  } else if (month.length === 2 && parseInt(month, 10) > 12) {
    month = '12';
  }

  if (year.length === 4) {
    const yearNum = parseInt(year, 10);
    if (yearNum < 1900) {
      year = '1900';
    } else if (yearNum > 2100) {
      year = '2100';
    }
  }

  const formattedDate = `${day}${month ? '/' + month : ''}${year ? '/' + year : ''}`;

  const time = digits.substring(8);

  if (time.length > 0) {
    const formattedTime = timeMask(time, validTimes);

    return `${formattedDate} ${formattedTime}`;
  }

  return formattedDate;
}

export function dateTimeUnmask(value: string): DateTime {
  return DateTime.fromFormat(value ?? '', DATE_TIME_FORMAT);
}
